import { type Project, newId } from '@portfolio-pm/shared';
import * as Y from 'yjs';
import { type WithNow, getProjectsMap } from './doc.ts';

export const INBOX_PROJECT_ID = 'p_inbox';

export interface CreateProjectInput {
  name: string;
  areaId?: string;
}

function timestamp(options: WithNow): number {
  return options.now ?? Date.now();
}

/** Read a project out of the Y.Doc into a plain object, or `undefined` if missing. */
export function getProject(doc: Y.Doc, id: string): Project | undefined {
  const map = getProjectsMap(doc).get(id);
  if (!map) return undefined;
  return projectFromMap(id, map);
}

/** Return all projects, sorted by order ascending. */
export function listProjects(doc: Y.Doc): Project[] {
  const projects: Project[] = [];
  for (const [id, map] of getProjectsMap(doc).entries()) {
    projects.push(projectFromMap(id, map));
  }
  projects.sort((a, b) => a.order - b.order);
  return projects;
}

/** Idempotently ensure the Inbox project exists. */
export function seedInbox(doc: Y.Doc, options: WithNow = {}): Project {
  const existing = getProject(doc, INBOX_PROJECT_ID);
  if (existing) return existing;
  const now = timestamp(options);
  const inner = new Y.Map<unknown>();
  inner.set('name', 'Inbox');
  inner.set('inbox', true);
  inner.set('archived', false);
  inner.set('order', 0);
  inner.set('createdAt', now);
  inner.set('updatedAt', now);
  getProjectsMap(doc).set(INBOX_PROJECT_ID, inner);
  return projectFromMap(INBOX_PROJECT_ID, inner);
}

/** Create a new project with a fresh id. */
export function createProject(
  doc: Y.Doc,
  input: CreateProjectInput,
  options: WithNow = {},
): Project {
  const now = timestamp(options);
  const id = newId();
  const inner = new Y.Map<unknown>();
  inner.set('name', input.name);
  if (input.areaId !== undefined) inner.set('areaId', input.areaId);
  inner.set('archived', false);
  inner.set('order', nextOrder(doc));
  inner.set('createdAt', now);
  inner.set('updatedAt', now);
  getProjectsMap(doc).set(id, inner);
  return projectFromMap(id, inner);
}

/** Rename a project (no-op if id missing). */
export function renameProject(doc: Y.Doc, id: string, name: string, options: WithNow = {}): void {
  const inner = getProjectsMap(doc).get(id);
  if (!inner) return;
  inner.set('name', name);
  inner.set('updatedAt', timestamp(options));
}

/** Archive a project (no-op if id missing). */
export function archiveProject(doc: Y.Doc, id: string, options: WithNow = {}): void {
  const inner = getProjectsMap(doc).get(id);
  if (!inner) return;
  inner.set('archived', true);
  inner.set('updatedAt', timestamp(options));
}

/** Unarchive a project (no-op if id missing). */
export function unarchiveProject(doc: Y.Doc, id: string, options: WithNow = {}): void {
  const inner = getProjectsMap(doc).get(id);
  if (!inner) return;
  inner.set('archived', false);
  inner.set('updatedAt', timestamp(options));
}

/** Persist a manual project order for the provided visible ids. */
export function reorderProjects(doc: Y.Doc, orderedIds: string[], options: WithNow = {}): void {
  const projectsMap = getProjectsMap(doc);
  const now = timestamp(options);
  doc.transact(() => {
    orderedIds.forEach((id, index) => {
      const inner = projectsMap.get(id);
      if (!inner) return;
      inner.set('order', index);
      inner.set('updatedAt', now);
    });
  });
}

// ─── Internals ────────────────────────────────────────────────────────────

function nextOrder(doc: Y.Doc): number {
  let max = 0;
  for (const inner of getProjectsMap(doc).values()) {
    const order = inner.get('order');
    if (typeof order === 'number' && order > max) max = order;
  }
  return max + 1;
}

function projectFromMap(id: string, inner: Y.Map<unknown>): Project {
  const project: Project = {
    id,
    name: String(inner.get('name') ?? ''),
    archived: Boolean(inner.get('archived')),
    order: Number(inner.get('order') ?? 0),
    createdAt: Number(inner.get('createdAt') ?? 0),
    updatedAt: Number(inner.get('updatedAt') ?? 0),
  };
  const areaId = inner.get('areaId');
  if (typeof areaId === 'string') project.areaId = areaId;
  const inbox = inner.get('inbox');
  if (inbox === true) project.inbox = true;
  return project;
}
